import { useState, type ReactNode } from 'react';
import { Page, PageHeader } from '../components/Page';
import { PCard } from '../components/PCard';
import { StatCard } from '../components/StatCard';
import { StatusPill } from '../components/StatusPill';
import { Async } from '../components/Async';
import { IcAlert, IcTag, IcPill } from '../components/icons';
import { useFetch } from '../lib/useFetch';
import { useAuth } from '../lib/auth';
import { fmtCents, fmtNum } from '../lib/format';
import {
  getClientOptions,
  getProductCatalog,
  getDeferredUsage,
  createPricing,
  ApiError,
  type CatalogProduct,
  type DeferredUsageItem,
} from '../lib/api';

const GLOBAL = 'global';

function ago(iso?: string): string {
  if (!iso) return '';
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  return hrs < 24 ? `${hrs}h ago` : `${Math.round(hrs / 24)}d ago`;
}

function day(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function reasonLabel(reason?: string): string {
  if (!reason) return 'Missing price';
  const text = reason.replace(/[_-]+/g, ' ').trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : 'Missing price';
}

export const PriceCoveragePage = ({ tabs }: { tabs?: ReactNode } = {}) => {
  const { can } = useAuth();
  const manage = can('pricing:manage');
  const [scope, setScope] = useState<string>(GLOBAL);
  const [onlyUnpriced, setOnlyUnpriced] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clientsState = useFetch(getClientOptions);
  const catalog = useFetch(
    () => getProductCatalog({ clientId: scope === GLOBAL ? undefined : scope }),
    [scope],
  );
  const deferred = useFetch(
    () => getDeferredUsage({ clientId: scope === GLOBAL ? undefined : scope }),
    [scope],
  );

  const clients = clientsState.data?.clients ?? [];
  const isClient = scope !== GLOBAL;
  const scopeLabel = isClient ? clients.find((c) => c.id === scope)?.name ?? 'Customer' : 'Global default';

  async function refresh() {
    setRefreshing(true);
    setError(null);
    try {
      await getProductCatalog({ clientId: isClient ? scope : undefined, fresh: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not refresh price coverage');
    } finally {
      catalog.reload();
      deferred.reload();
      setRefreshing(false);
    }
  }

  async function setPrice(p: CatalogProduct, priceCents: number) {
    setError(null);
    try {
      await createPricing({
        clientId: isClient ? scope : null,
        drug: p.drug,
        form: p.form,
        strength: p.strength,
        size: p.size,
        costCents: 0,
        priceCents,
      });
      catalog.reload();
      deferred.reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save price');
    }
  }

  return (
    <Page>
      <PageHeader
        tabs={tabs}
        title="Price Coverage"
        sub="Products dispensed in the last 90 days vs what's priced. Pick a customer to set their rates; unset products fall back to global."
        actions={
          <>
            {catalog.data?.computedAt && (
              <span style={{ fontSize: 12, color: 'var(--p-muted-fg)', alignSelf: 'center' }}>
                Updated {ago(catalog.data.computedAt)}
              </span>
            )}
            <button className="btn" onClick={refresh} disabled={refreshing}>
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
            <select className="select" value={scope} onChange={(e) => setScope(e.target.value)}>
              <option value={GLOBAL}>Global default</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.type})
                </option>
              ))}
            </select>
          </>
        }
      />

      {error && (
        <div style={{ padding: '8px 12px', borderRadius: 6, background: 'rgba(239,68,68,.08)', color: 'var(--status-error-ink,#b91c1c)', fontSize: 13 }}>
          {error}
        </div>
      )}

      <PCard>
        <div
          style={{
            padding: 16,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 16,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, minWidth: 0 }}>
            <div className="p-stat__icon p-stat__icon--attention" aria-hidden="true">
              <IcAlert size={18} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 650 }}>Deferred pharmacy usage</h3>
              <p style={{ margin: '3px 0 0', color: 'var(--p-muted-fg)', fontSize: 12, lineHeight: 1.5 }}>
                These items were held out of invoices because they could not be priced. Add a matching price and they will retry on a future billing run.
              </p>
            </div>
          </div>
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <div
              className="tabular"
              style={{ color: 'var(--status-attention-ink)', fontSize: 24, fontWeight: 700, lineHeight: 1 }}
            >
              {deferred.data ? fmtNum(deferred.data.total) : '—'}
            </div>
            <div style={{ marginTop: 4, color: 'var(--p-muted-fg)', fontSize: 11 }}>
              waiting {isClient ? `for ${scopeLabel}` : 'across customers'}
            </div>
          </div>
        </div>
        <div style={{ borderTop: '1px solid var(--p-border)' }}>
          <Async
            state={deferred}
            isEmpty={(data) => data.items.length === 0}
            emptyLabel={`No deferred pharmacy usage${isClient ? ` for ${scopeLabel}` : ''}.`}
          >
            {(data) => (
              <div style={{ overflowX: 'auto' }}>
                <table className="p-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      {!isClient && <th>Customer</th>}
                      <th>Order</th>
                      <th style={{ textAlign: 'right' }}>Qty</th>
                      <th>Waiting since</th>
                      <th>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((item) => (
                      <DeferredUsageRow key={item.id} item={item} showClient={!isClient} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Async>
        </div>
      </PCard>

      <Async state={catalog} isEmpty={(d) => d.products.length === 0} emptyLabel="No products dispensed in this window.">
        {(data) => {
          const cov = data.coverage;
          const volPct = cov.totalVolume ? Math.round((cov.pricedVolume / cov.totalVolume) * 100) : 0;
          const rows = onlyUnpriced ? data.products.filter((p) => !p.priced) : data.products;
          return (
            <>
              <div className="grid grid-3">
                <StatCard tone="staged" icon={IcTag} label="Products priced" value={`${cov.pricedProducts}/${cov.totalProducts}`} sub={`for ${scopeLabel}`} />
                <StatCard tone="in-transit" icon={IcPill} label="Volume covered" value={`${volPct}%`} sub={`${fmtNum(cov.pricedVolume)} of ${fmtNum(cov.totalVolume)} fills`} />
                <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <label style={{ fontSize: 13, color: 'var(--p-muted-fg)', display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input type="checkbox" checked={onlyUnpriced} onChange={(e) => setOnlyUnpriced(e.target.checked)} />
                    Show only unpriced
                  </label>
                </div>
              </div>

              <PCard>
                <table className="p-table">
                  <thead>
                    <tr>
                      <th>Drug</th>
                      <th>Conc.</th>
                      <th>Size</th>
                      <th style={{ textAlign: 'right' }}>90d vol</th>
                      <th>Status</th>
                      <th style={{ textAlign: 'right' }}>Price</th>
                      {manage && <th style={{ textAlign: 'right' }}>Set price</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((p) => (
                      <ProductRow key={p.key} p={p} isClient={isClient} manage={manage} onSave={setPrice} />
                    ))}
                  </tbody>
                </table>
              </PCard>
            </>
          );
        }}
      </Async>
    </Page>
  );
};

function DeferredUsageRow({ item, showClient }: { item: DeferredUsageItem; showClient: boolean }) {
  const detail = [item.form, item.strength, item.size].filter(Boolean).join(' · ');
  return (
    <tr>
      <td style={{ minWidth: 210 }} title={item.usageKey}>
        <div style={{ fontWeight: 500 }}>{item.drug}</div>
        <div style={{ marginTop: 2, color: 'var(--p-muted-fg)', fontSize: 11 }}>{detail || '—'}</div>
      </td>
      {showClient && <td style={{ minWidth: 140 }}>{item.clientName ?? item.clientId}</td>}
      <td style={{ minWidth: 130 }}>
        <div className="tabular" style={{ fontFamily: 'var(--font-product-mono)', fontSize: 12 }}>{item.orderId}</div>
        {item.clinic && <div style={{ marginTop: 2, color: 'var(--p-muted-fg)', fontSize: 11 }}>{item.clinic}</div>}
      </td>
      <td className="tabular" style={{ textAlign: 'right' }}>{fmtNum(item.quantity)}</td>
      <td style={{ minWidth: 125 }}>
        <div>{day(item.firstSeenAt)}</div>
        {item.lastSeenAt !== item.firstSeenAt && (
          <div style={{ marginTop: 2, color: 'var(--p-muted-fg)', fontSize: 11 }}>Seen {ago(item.lastSeenAt)}</div>
        )}
      </td>
      <td><StatusPill tone="attention">{reasonLabel(item.reason)}</StatusPill></td>
    </tr>
  );
}

function ProductRow({
  p,
  isClient,
  manage,
  onSave,
}: {
  p: CatalogProduct;
  isClient: boolean;
  manage: boolean;
  onSave: (p: CatalogProduct, priceCents: number) => Promise<void>;
}) {
  const effective = p.clientPriceCents ?? p.globalPriceCents;
  const inherited = isClient && p.clientPriceCents == null && p.globalPriceCents != null;
  const [val, setVal] = useState('');
  const [busy, setBusy] = useState(false);

  async function save() {
    const dollars = parseFloat(val);
    if (Number.isNaN(dollars) || dollars < 0) return;
    setBusy(true);
    try {
      await onSave(p, Math.round(dollars * 100));
      setVal('');
    } finally {
      setBusy(false);
    }
  }

  const statusPill = p.scope === 'flat' ? (
    <StatusPill tone="in-progress">Flat</StatusPill>
  ) : !p.priced ? (
    <StatusPill tone="error">Unpriced</StatusPill>
  ) : inherited ? (
    <StatusPill tone="neutral">Global</StatusPill>
  ) : p.scope === 'client' ? (
    <StatusPill tone="in-progress">Override</StatusPill>
  ) : (
    <StatusPill tone="neutral">Global</StatusPill>
  );

  return (
    <tr>
      <td style={{ fontWeight: 500 }}>
        {p.drug}
        <div className="muted" style={{ fontSize: 11 }} title={p.sampleName}>{p.form}</div>
      </td>
      <td className="muted">{p.strength ?? '—'}</td>
      <td className="muted">{p.size ?? '—'}</td>
      <td className="tabular muted" style={{ textAlign: 'right' }}>{fmtNum(p.volume)}</td>
      <td>{statusPill}</td>
      <td className="tabular" style={{ textAlign: 'right' }}>{effective != null ? fmtCents(effective) : '—'}</td>
      {manage && p.scope === 'flat' && (
        <td className="muted" style={{ textAlign: 'right', fontSize: 12 }}>Flat rate</td>
      )}
      {manage && p.scope !== 'flat' && (
        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
          <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
            <span style={{ color: 'var(--p-muted-fg)', fontSize: 12 }}>$</span>
            <input
              className="select"
              style={{ width: 80 }}
              type="number"
              step="0.01"
              min="0"
              value={val}
              placeholder={isClient ? 'override' : 'price'}
              onChange={(e) => setVal(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void save(); }}
            />
            <button className="btn" onClick={() => void save()} disabled={busy || !val}>
              {busy ? '…' : effective != null ? 'Update' : 'Set'}
            </button>
          </div>
        </td>
      )}
    </tr>
  );
}
